if (!customElements.get('hero-banner-slider')) {
  class HeroBannerSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.hbs__slide'));
      this.dots = Array.from(this.querySelectorAll('.hbs__dot'));
      if (this.slides.length === 0) return;

      const activeIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.current = activeIndex > -1 ? activeIndex : 0;
      this.duration = (parseInt(this.dataset.speed, 10) || 6) * 1000;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.remaining = this.duration;
      this.timer = null;
      this.startedAt = 0;
      this.holds = new Set(); // reasons the autoplay is currently paused

      this.onVisibilityChange = this.onVisibilityChange.bind(this);

      this.bindPagination();
      this.bindKeyboard();
      this.bindSwipe();
      this.bindPauseEvents();
      this.bindThemeEditor();

      this.goTo(this.current, true);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    /* ---------- Navigation ---------- */

    goTo(index, initial = false) {
      const total = this.slides.length;
      const nextIndex = ((index % total) + total) % total;

      this.slides.forEach((slide, i) => {
        const isActive = i === nextIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.inert = !isActive;
      });

      this.dots.forEach((dot, i) => {
        const isActive = i === nextIndex;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      // Restart the progress line animation when the same dot stays active
      if (nextIndex === this.current && !initial) this.restartProgress();

      this.current = nextIndex;
      this.resetTimer();
    }

    next() {
      this.goTo(this.current + 1);
    }

    prev() {
      this.goTo(this.current - 1);
    }

    restartProgress() {
      const fill = this.dots[this.current]?.querySelector('.hbs__dot-fill');
      if (!fill) return;
      fill.style.animation = 'none';
      void fill.offsetWidth; // force reflow
      fill.style.animation = '';
    }

    /* ---------- Autoplay timer ---------- */

    resetTimer() {
      clearTimeout(this.timer);
      this.timer = null;
      this.remaining = this.duration;
      this.startTimer();
    }

    startTimer() {
      if (!this.autoplay || this.holds.size > 0) return;
      clearTimeout(this.timer);
      this.startedAt = performance.now();
      this.timer = setTimeout(() => this.next(), this.remaining);
    }

    stopTimer() {
      if (!this.timer) return;
      clearTimeout(this.timer);
      this.timer = null;
      this.remaining = Math.max(0, this.remaining - (performance.now() - this.startedAt));
    }

    hold(reason) {
      if (!this.autoplay) return;
      this.holds.add(reason);
      this.stopTimer();
      this.classList.add('is-paused');
    }

    release(reason) {
      if (!this.autoplay) return;
      this.holds.delete(reason);
      if (this.holds.size === 0) {
        this.classList.remove('is-paused');
        this.startTimer();
      }
    }

    /* ---------- Events ---------- */

    bindPagination() {
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.goTo(i));
      });
    }

    bindKeyboard() {
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.next();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.prev();
        }
      });
    }

    bindSwipe() {
      let startX = 0;
      let startY = 0;

      this.addEventListener(
        'touchstart',
        (event) => {
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
        },
        { passive: true }
      );

      this.addEventListener(
        'touchend',
        (event) => {
          const diffX = event.changedTouches[0].clientX - startX;
          const diffY = event.changedTouches[0].clientY - startY;
          if (Math.abs(diffX) < 50 || Math.abs(diffX) < Math.abs(diffY)) return;
          if (diffX < 0) {
            this.next();
          } else {
            this.prev();
          }
        },
        { passive: true }
      );
    }

    bindPauseEvents() {
      if (this.dataset.pauseOnHover === 'true') {
        this.addEventListener('mouseenter', () => this.hold('hover'));
        this.addEventListener('mouseleave', () => this.release('hover'));
      }

      // Pause only for keyboard focus, so a mouse click on a number doesn't stop autoplay
      this.addEventListener('focusin', (event) => {
        if (event.target.matches(':focus-visible')) this.hold('focus');
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.release('focus');
      });

      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.hold('hidden');
      } else {
        this.release('hidden');
      }
    }

    bindThemeEditor() {
      if (!window.Shopify || !Shopify.designMode) return;

      this.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.indexOf(event.target);
        if (index === -1) return;
        this.hold('editor');
        this.goTo(index);
      });

      this.addEventListener('shopify:block:deselect', () => this.release('editor'));
    }
  }

  customElements.define('hero-banner-slider', HeroBannerSlider);
}
